import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, ArrowUpRight, Clock } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import SectionHeading from "./SectionHeading";

const ease = [0.16, 1, 0.3, 1];
const externalUrl = (u) => (u && !/^https?:\/\//i.test(u) ? `https://${u}` : u);
const openExternal = (e, u) => { e.preventDefault(); const url = externalUrl(u); if (url) window.open(url, "_blank", "noopener,noreferrer"); };

export default function Location() {
  const wedding = useContent();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="ubicacion" ref={ref} className="bg-paper py-24 sm:py-36">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
        <SectionHeading number="01" kicker="Dónde y Cuándo" title="Fecha & Ubicación" />

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          {/* Details */}
          <div className="lg:col-span-5 lg:pb-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease }}
            >
              <p className="overline text-[color:var(--muted)]">Cuándo</p>
              <h3 className="font-display text-3xl sm:text-4xl mt-4 tracking-tight" data-testid="location-date">
                {wedding.dateLabel}
              </h3>
              <p className="mt-6 font-light text-[color:var(--muted)] max-w-sm leading-relaxed flex gap-3">
                <Clock size={18} strokeWidth={1.4} className="mt-1 shrink-0" />
                <span data-testid="location-schedule">
                  <span className="text-ink">{wedding.venue.ceremonyTime}</span> · Ceremonia
                  <br />
                  <span className="text-ink">{wedding.venue.receptionTime}</span> · Celebración
                </span>
              </p>

              <div className="mt-10 border-t border-black/10 pt-10">
                <p className="overline text-[color:var(--muted)]">{wedding.venue.line1}</p>
                <h3 className="font-display text-3xl sm:text-4xl mt-4 tracking-tight">
                  {wedding.venue.name}
                </h3>
                <p className="mt-6 font-light text-[color:var(--muted)] max-w-sm leading-relaxed flex gap-3">
                  <MapPin size={18} strokeWidth={1.4} className="mt-1 shrink-0" />
                  <span>{wedding.venue.address}</span>
                </p>
                <p className="mt-4 text-sm font-light text-[color:var(--muted)] max-w-sm">
                  {wedding.venue.note}
                </p>

                <a
                  href={externalUrl(wedding.venue.mapsUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => openExternal(e, wedding.venue.mapsUrl)}
                  className="group mt-10 inline-flex items-center gap-3 bg-ink text-white px-8 py-4 hover:bg-white hover:text-ink border border-ink transition-colors duration-300"
                  data-testid="open-google-maps"
                >
                  <span className="overline">Abrir en Google Maps</span>
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </a>
              </div>
            </motion.div>
          </div>

          {/* Image */}
          <div className="lg:col-span-7">
            <div className="relative overflow-hidden aspect-[4/5] sm:aspect-[16/10] bg-secondary">
              <motion.img
                src={wedding.locationImage}
                alt={wedding.venue.name}
                style={{ y: imgY }}
                className="absolute inset-0 w-full h-[116%] object-cover"
                data-testid="location-image"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
